<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: HaplogroupGlobe                                       |
| Section: components                                              |
| Purpose: Globe visualization for DNA haplogroup distribution.    |
+------------------------------------------------------------------+
-->
<template>
    <div class="haplogroup-globe-wrapper">
        <div ref="mapContainer" class="haplogroup-globe-map"></div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import haplogroupData from '@/data/haplogroups.json';

mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;

export default {
    name: 'HaplogroupGlobe',
    props: {
        selectedHaplogroupIds: {
            type: Array,
            default: null,
        },
        focusedHaplogroupId: {
            type: String,
            default: '',
        },
    },
    emits: ['update:selectedHaplogroupIds'],
    data() {
        return {
            localSelectedHaplogroupIds: (haplogroupData.haplogroups || [])
                .filter(hg => hg.routeAvailable !== false && Array.isArray(hg.steps) && hg.steps.length > 0)
                .map(hg => hg.id),
            mapInstance: null,
            haplogroupLayerIds: [],
            currentHaplogroupPopup: null,
            mapboxNavigationControl: null,
            mapboxFullscreenControl: null,
        };
    },
    computed: {
        availableHaplogroups() {
            return (haplogroupData.haplogroups || [])
                .filter(hg => hg.routeAvailable !== false && Array.isArray(hg.steps) && hg.steps.length > 0);
        },
        selectedHaplogroupIdsProxy: {
            get() {
                return Array.isArray(this.selectedHaplogroupIds)
                    ? this.selectedHaplogroupIds
                    : this.localSelectedHaplogroupIds;
            },
            set(nextIds) {
                this.localSelectedHaplogroupIds = nextIds;
                this.$emit('update:selectedHaplogroupIds', nextIds);
            },
        },
    },
    watch: {
        selectedHaplogroupIdsProxy() {
            if (this.mapInstance) {
                this.loadHaplogroupLayers();
            }
        },
        focusedHaplogroupId(newId) {
            if (newId) {
                this.focusHaplogroupById(newId);
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.initializeMapbox();
        });
    },
    beforeUnmount() {
        if (this.mapInstance) {
            this.mapInstance.remove();
            this.mapInstance = null;
        }
    },
    methods: {
        initializeMapbox() {
            if (!this.$refs.mapContainer) return;
            if (!mapboxgl.accessToken) {
                console.warn('VUE_APP_MAPBOX_TOKEN is required to initialize Mapbox.');
                return;
            }

            this.mapInstance = new mapboxgl.Map({
                container: this.$refs.mapContainer,
                style: 'mapbox://styles/mapbox/standard',
                center: [20, 10],
                zoom: 1.5,
                pitch: 0,
                bearing: 0,
                projection: 'globe',
                cooperativeGestures: true,
            });

            this.mapInstance.on('load', () => {
                this.mapInstance.setFog({
                    color: 'rgb(186, 210, 235)',
                    'high-color': 'rgb(36, 92, 223)',
                    'horizon-blend': 0.05,
                    'space-color': 'rgb(8, 8, 20)',
                    'star-intensity': 1.0,
                });

                if (!this.mapboxNavigationControl) {
                    this.mapboxNavigationControl = new mapboxgl.NavigationControl();
                    this.mapInstance.addControl(this.mapboxNavigationControl, 'top-right');
                }

                this.loadHaplogroupLayers();
            });
        },

        loadHaplogroupLayers() {
            if (!this.mapInstance) return;

            this.removeHaplogroupLayers();

            const selectedHaplogroups = this.availableHaplogroups.filter(hg =>
                this.selectedHaplogroupIdsProxy.includes(hg.id)
            );

            if (selectedHaplogroups.length === 0) return;

            selectedHaplogroups.forEach((haplogroup, index) => {
                const color = haplogroup.lineType === 'paternal' ? '#0085ca' : '#e0004d';

                // Line features connecting steps chronologically
                const lineFeatures = [];
                for (let i = 0; i < haplogroup.steps.length - 1; i++) {
                    const current = haplogroup.steps[i];
                    const next = haplogroup.steps[i + 1];
                    lineFeatures.push({
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [current.coordinates, next.coordinates],
                        },
                        properties: {
                            haplogroupId: haplogroup.id,
                            fromHaplogroup: current.haplogroup,
                            toHaplogroup: next.haplogroup,
                            fromAge: current.age,
                            toAge: next.age,
                        },
                    });
                }

                // Point features for each step
                const pointFeatures = haplogroup.steps.map((step, stepIndex) => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: step.coordinates,
                    },
                    properties: {
                        haplogroupId: haplogroup.id,
                        haplogroup: step.haplogroup,
                        age: step.age,
                        era: step.era,
                        location: step.location,
                        testedDescendants: step.testedDescendants,
                        ancientConnections: step.ancientConnections,
                        personName: haplogroup.personName,
                        lineType: haplogroup.lineType,
                        stepNumber: stepIndex + 1,
                        markerLabel: this.getHaplogroupMarkerLabel(step),
                    },
                }));

                // Add line layer
                const lineSourceId = `hg-lines-${index}`;
                const lineLayerId = `hg-line-layer-${index}`;

                try {
                    this.mapInstance.addSource(lineSourceId, {
                        type: 'geojson',
                        data: { type: 'FeatureCollection', features: lineFeatures },
                    });

                    this.mapInstance.addLayer({
                        id: lineLayerId,
                        type: 'line',
                        source: lineSourceId,
                        paint: {
                            'line-color': color,
                            'line-width': 3,
                            'line-opacity': 0.6,
                        },
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round',
                        },
                    });

                    const arrowLayerId = `hg-arrows-${index}`;
                    this.mapInstance.addLayer({
                        id: arrowLayerId,
                        type: 'symbol',
                        source: lineSourceId,
                        layout: {
                            'symbol-placement': 'line',
                            'symbol-spacing': 80,
                            'text-field': '←',
                            'text-size': 28,
                            'text-keep-upright': false,
                            'text-rotation-alignment': 'map',
                            'text-pitch-alignment': 'map',
                        },
                        paint: {
                            'text-color': color,
                            'text-opacity': 0.8,
                            'text-translate': [0, -10],
                        },
                    });

                    this.haplogroupLayerIds.push(arrowLayerId, lineLayerId, lineSourceId);
                } catch (err) {
                    console.warn(`Failed to add haplogroup line layer for ${haplogroup.id}:`, err);
                }

                // Add point layer
                const pointSourceId = `hg-points-${index}`;
                const pointLayerId = `hg-point-layer-${index}`;

                try {
                    this.mapInstance.addSource(pointSourceId, {
                        type: 'geojson',
                        data: { type: 'FeatureCollection', features: pointFeatures },
                    });

                    this.mapInstance.addLayer({
                        id: pointLayerId,
                        type: 'circle',
                        source: pointSourceId,
                        paint: {
                            'circle-radius': 12,
                            'circle-color': color,
                            'circle-stroke-width': 2,
                            'circle-stroke-color': '#ffffff',
                            'circle-opacity': 0.85,
                            'circle-pitch-alignment': 'map',
                        },
                    });

                    this.mapInstance.on('mouseenter', pointLayerId, () => {
                        this.mapInstance.getCanvas().style.cursor = 'pointer';
                    });
                    this.mapInstance.on('mouseleave', pointLayerId, () => {
                        this.mapInstance.getCanvas().style.cursor = '';
                    });

                    this.mapInstance.on('click', pointLayerId, (e) => {
                        if (e.features.length === 0) return;
                        const props = e.features[0].properties;
                        const formattedAge = props.age
                            .replace('BCE', this.$t('placeMap.bce'))
                            .replace('AD', this.$t('placeMap.ad'));
                        const translatedLocation = this.translateLocation(props.location);
                        const translatedEra = this.translateEra(props.era);
                        const lineTypePath = props.lineType === 'paternal' ? 'y-dna' : 'mtdna';
                        const ftdnaUrl = `https://discover.familytreedna.com/${lineTypePath}/${encodeURIComponent(props.haplogroup)}/story`;

                        new mapboxgl.Popup()
                            .setLngLat(e.lngLat)
                            .setHTML(`
                                <div style="font-size: 12px; max-width: 250px;">
                                    <strong>${props.haplogroup}</strong><br/>
                                    <strong>${formattedAge}</strong> (${translatedEra})<br/>
                                    <em>${translatedLocation}</em><br/>
                                    ${props.testedDescendants ? `<br/>${props.testedDescendants.toLocaleString()} ${this.$t('placeMap.testedDescendants')}` : ''}
                                    ${props.ancientConnections ? `<br/>${props.ancientConnections} ${this.$t('placeMap.ancientConnections')}` : ''}
                                    <br/><br/>
                                    <a href="${ftdnaUrl}" target="_blank" rel="noopener noreferrer" style="color: #c62828; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                                        ${this.$t('placeMap.moreInfo')}
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style="width: 12px; height: 12px; fill: #c62828;">
                                            <path d="M384 64C366.3 64 352 78.3 352 96C352 113.7 366.3 128 384 128L466.7 128L265.3 329.4C252.8 341.9 252.8 362.2 265.3 374.7C277.8 387.2 298.1 387.2 310.6 374.7L512 173.3L512 256C512 273.7 526.3 288 544 288C561.7 288 576 273.7 576 256L576 96C576 78.3 561.7 64 544 64L384 64zM144 160C99.8 160 64 195.8 64 240L64 496C64 540.2 99.8 576 144 576L400 576C444.2 576 480 540.2 480 496L480 416C480 398.3 465.7 384 448 384C430.3 384 416 398.3 416 416L416 496C416 504.8 408.8 512 400 512L144 512C135.2 512 128 504.8 128 496L128 240C128 231.2 135.2 224 144 224L224 224C241.7 224 256 209.7 256 192C256 174.3 241.7 160 224 160L144 160z"/>
                                        </svg>
                                    </a>
                                    <br/><br/>
                                    <small>${props.personName} • ${props.lineType === 'paternal' ? this.$t('placeMap.paternalLineDot') : this.$t('placeMap.maternalLineDot')}</small>
                                </div>
                            `)
                            .addTo(this.mapInstance);
                    });

                    const labelLayerId = `hg-label-layer-${index}`;
                    this.mapInstance.addLayer({
                        id: labelLayerId,
                        type: 'symbol',
                        source: pointSourceId,
                        layout: {
                            'text-field': ['get', 'markerLabel'],
                            'text-size': 11,
                            'text-allow-overlap': true,
                            'text-ignore-placement': false,
                            'text-pitch-alignment': 'map',
                            'text-max-width': 8,
                            'text-anchor': 'top',
                            'text-offset': [0, 1.7],
                        },
                        paint: {
                            'text-color': '#000000',
                            'text-halo-color': '#ffffff',
                            'text-halo-width': 2,
                        },
                    });

                    this.haplogroupLayerIds.push(labelLayerId, pointLayerId, pointSourceId);
                } catch (err) {
                    console.warn(`Failed to add haplogroup point layer for ${haplogroup.id}:`, err);
                }
            });
        },

        removeHaplogroupLayers() {
            if (!this.mapInstance) return;

            this.haplogroupLayerIds.forEach(id => {
                if (this.mapInstance.getLayer(id)) {
                    this.mapInstance.removeLayer(id);
                } else if (this.mapInstance.getSource(id)) {
                    this.mapInstance.removeSource(id);
                }
            });

            this.haplogroupLayerIds = [];
        },

        focusHaplogroupById(haplogroupId) {
            const haplogroup = this.availableHaplogroups.find(hg => hg.id === haplogroupId);
            const firstStep = haplogroup?.steps?.[0];
            if (!haplogroup || !firstStep) return;

            if (!this.selectedHaplogroupIdsProxy.includes(haplogroupId)) {
                this.selectedHaplogroupIdsProxy = [...this.selectedHaplogroupIdsProxy, haplogroupId];
            }

            this.flyToHaplogroupStep(firstStep);
        },

        flyToHaplogroupStep(step) {
            if (!this.mapInstance || !step.coordinates) return;

            const [lng, lat] = step.coordinates;

            this.mapInstance.flyTo({
                center: [lng, lat],
                zoom: 4,
                duration: 1500,
                essential: true,
            });

            setTimeout(() => {
                if (this.currentHaplogroupPopup) {
                    this.currentHaplogroupPopup.remove();
                    this.currentHaplogroupPopup = null;
                }

                const haplogroup = this.availableHaplogroups.find(hg =>
                    hg.steps.some(s => s.haplogroup === step.haplogroup)
                );
                if (!haplogroup) return;

                const formattedAge = step.age
                    .replace('BCE', this.$t('placeMap.bce'))
                    .replace('AD', this.$t('placeMap.ad'));
                const lineTypeText = haplogroup.lineType === 'paternal'
                    ? this.$t('placeMap.paternalLineDot')
                    : this.$t('placeMap.maternalLineDot');
                const translatedLocation = this.translateLocation(step.location);
                const translatedEra = this.translateEra(step.era);
                const lineTypePath = haplogroup.lineType === 'paternal' ? 'y-dna' : 'mtdna';
                const ftdnaUrl = `https://discover.familytreedna.com/${lineTypePath}/${encodeURIComponent(step.haplogroup)}/story`;

                this.currentHaplogroupPopup = new mapboxgl.Popup()
                    .setLngLat([lng, lat])
                    .setHTML(`
                        <div style="font-size: 12px; max-width: 250px;">
                            <strong>${step.haplogroup}</strong><br/>
                            <strong>${formattedAge}</strong> (${translatedEra})<br/>
                            <em>${translatedLocation}</em><br/>
                            ${step.testedDescendants ? `<br/>${step.testedDescendants.toLocaleString()} ${this.$t('placeMap.testedDescendants')}` : ''}
                            ${step.ancientConnections ? `<br/>${step.ancientConnections} ${this.$t('placeMap.ancientConnections')}` : ''}
                            <br/><br/>
                            <a href="${ftdnaUrl}" target="_blank" rel="noopener noreferrer" style="color: #c62828; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                                ${this.$t('placeMap.moreInfo')}
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" style="width: 12px; height: 12px; fill: #c62828;">
                                    <path d="M384 64C366.3 64 352 78.3 352 96C352 113.7 366.3 128 384 128L466.7 128L265.3 329.4C252.8 341.9 252.8 362.2 265.3 374.7C277.8 387.2 298.1 387.2 310.6 374.7L512 173.3L512 256C512 273.7 526.3 288 544 288C561.7 288 576 273.7 576 256L576 96C576 78.3 561.7 64 544 64L384 64zM144 160C99.8 160 64 195.8 64 240L64 496C64 540.2 99.8 576 144 576L400 576C444.2 576 480 540.2 480 496L480 416C480 398.3 465.7 384 448 384C430.3 384 416 398.3 416 416L416 496C416 504.8 408.8 512 400 512L144 512C135.2 512 128 504.8 128 496L128 240C128 231.2 135.2 224 144 224L224 224C241.7 224 256 209.7 256 192C256 174.3 241.7 160 224 160L144 160z"/>
                                </svg>
                            </a>
                            <br/><br/>
                            <small>${haplogroup.personName} • ${lineTypeText}</small>
                        </div>
                    `)
                    .addTo(this.mapInstance);
            }, 1600);
        },

        getHaplogroupMarkerLabel(step) {
            const formattedAge = step.age
                .replace('BCE', this.$t('placeMap.bce'))
                .replace('AD', this.$t('placeMap.ad'))
                .replace(' ', '');
            return `${step.haplogroup}\n${formattedAge}`;
        },

        getHaplogroupLineText(hg) {
            const lineText = hg.lineType === 'paternal'
                ? this.$t('placeMap.paternalLineMiddot')
                : this.$t('placeMap.maternalLineMiddot');
            return `${hg.personName} · ${lineText}`;
        },

        formatHaplogroupAge(age) {
            if (!age) return '';
            return age.replace('BCE', this.$t('placeMap.bce')).replace('AD', this.$t('placeMap.ad'));
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
        },

        translateLocation(location) {
            const keyMap = {
                'Northern Europe': 'northernEurope',
                'Scandinavia': 'scandinavia',
                'Central Europe': 'centralEurope',
                'Balkans': 'balkans',
                'Eastern Mediterranean': 'easternMediterranean',
                'Middle East': 'middleEast',
                'West Asia': 'westAsia',
                'Northeast Africa': 'northeastAfrica',
                'East Africa': 'eastAfrica',
                'Japan': 'japan',
                'Korea': 'korea',
                'Southern China': 'southernChina',
                'Southwest China': 'southwestChina',
                'Northeast Asia': 'northeastAsia',
                'East Asia': 'eastAsia',
                'Southeast Asia': 'southeastAsia',
                'South Asia': 'southAsia',
                'East Africa/Middle East': 'eastAfricaMiddleEast',
            };
            const key = keyMap[location];
            return key ? this.$t(`placeMap.locations.${key}`) : location;
        },

        translateEra(era) {
            const keyMap = {
                'Metal Ages': 'metalAges',
                'Stone Age': 'stoneAge',
                'Stone Age/Metal Ages': 'stoneAgeMetalAges',
                'Before Homo Sapiens': 'beforeHomoSapiens',
                'Before Homo Sapiens/Early Homo Sapiens': 'beforeHomoSapiensEarlyHomoSapiens',
                'Early Homo Sapiens/Stone Age': 'earlyHomoSapiensStoneAge',
            };
            const key = keyMap[era];
            return key ? this.$t(`placeMap.eras.${key}`) : era;
        },
    },
};
</script>

<style scoped>
.haplogroup-globe-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: var(--border-radius-xl);
    overflow: hidden;
    position: relative;
}

.haplogroup-globe-map {
    flex: 1;
    min-height: 0;
    position: relative;
}

:deep(.mapboxgl-popup-content) {
    padding: 12px !important;
    border-radius: 8px;
}

:deep(.mapboxgl-popup-tip) {
    border-top-color: #fff;
}
</style>
